import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Braces } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await register(username.trim(), password);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 grid-lines pointer-events-none" />
        <div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-30 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(142 71% 45% / 0.3), transparent 60%)" }}
        />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="w-8 h-8 rounded-sm bg-accent text-accent-foreground grid place-items-center">
            <Braces className="w-4 h-4" strokeWidth={2} />
          </div>
          <span className="font-display font-bold text-xl">MLForge</span>
        </Link>
        <div className="relative">
          <div className="label-mono mb-3">// GET STARTED</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter leading-[1.05] max-w-md">
            Pick a username.<br />Start shipping models.
          </h1>
          <p className="text-sm text-muted-foreground mt-4 max-w-md leading-relaxed">
            Each account gets its own isolated workspace — projects, datasets and trained pipelines are private to you.
          </p>
        </div>
        <div className="label-mono relative">v1.1 · JWT-secured</div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="register-form">
          <div>
            <div className="label-mono mb-2">// CREATE ACCOUNT</div>
            <h2 className="font-display font-bold text-3xl tracking-tighter">Sign up</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Already registered? <Link to="/login" className="text-accent hover:underline" data-testid="link-login">Sign in</Link>.
            </p>
          </div>
          <div>
            <Label className="label-mono">Username</Label>
            <Input
              data-testid="register-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              placeholder="3-32 chars: a-z 0-9 _ . -"
              className="mt-2 bg-background"
            />
          </div>
          <div>
            <Label className="label-mono">Password</Label>
            <Input
              data-testid="register-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Minimum 6 characters"
              className="mt-2 bg-background"
            />
          </div>
          {error && (
            <div className="text-xs text-destructive border border-destructive/30 bg-destructive/5 rounded-sm px-3 py-2" data-testid="register-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="register-submit"
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm h-11"
          >
            {busy ? "Creating…" : (<>Create account <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>
        </form>
      </div>
    </div>
  );
}
